import { api } from '../api';
import { sanitizeDashboardPayload } from './sanitizers';
import { CreateDashboardPayloadType } from './types';

// SUMMARY:
// Endpoint for getting all dashboards for the customer.
const getAll = async ({ customerId }: { customerId: string }) => {
  try {
    const response = await api({
      method: 'get',
      url: `/customers/${customerId}/dashboards`,
    });
    return response.data;
  } catch (error) {
    console.error(error);
    return [];
  }
};

//Endpoint for creating dashboard for the customer.
const createDashboard = async ({
  customerId,
  data,
}: {
  customerId: string;
  data: CreateDashboardPayloadType;
}) => {
  try {
    const sanitizedData = sanitizeDashboardPayload(data);

    const response = await api({
      method: 'post',
      url: `/customers/${customerId}/dashboards/layout`,
      data: sanitizedData,
    });

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

//Endpoint for editing dashboard for the customer.
const editDashboard = async ({
  dashboardId,
  customerId,
  data,
}: {
  dashboardId: string;
  customerId: string;
  data: CreateDashboardPayloadType;
}) => {
  try {
    const sanitizedData = sanitizeDashboardPayload(data);

    const response = await api({
      method: 'put',
      url: `/customers/${customerId}/dashboards/${dashboardId}/layout`,
      data: sanitizedData,
    });

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

// Endpoint for getting dashboard by dashboardId.
const getById = async ({
  customerId,
  dashboardId,
}: {
  customerId: string;
  dashboardId: string;
}) => {
  try {
    const response = await api({
      method: 'get',
      url: `/customers/${customerId}/dashboards/${dashboardId}`,
    });
    return response.data;
  } catch (error) {
    console.error(error);
    return {};
  }
};

//Endpoint for deleting dashboard
const deleteDashboard = async ({
  customerId,
  dashboardId,
}: {
  customerId: string;
  dashboardId: string;
}) => {
  try {
    const response = await api({
      method: 'delete',
      url: `/customers/${customerId}/dashboards/${dashboardId}`,
    });
    return response.data;
  } catch (error) {
    console.error(error);
    return {};
  }
};

// Endpoint for getting presigned PUT url for
// uploading directly to S3
const getUploadUrl = async ({
  customerId,
  fileName,
  contentType,
}: {
  customerId: string;
  fileName: string;
  contentType: string;
}) => {
  try {
    const response = await api({
      method: 'post',
      url: `/customers/${customerId}/dashboards/presigned-url`,
      data: {
        fileName,
        contentType,
      },
    });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

// Upload a file to S3 with presigned url
const uploadFileToS3 = async (
  presignedUrl: string,
  file: File,
  contentType: string
): Promise<boolean> => {
  try {
    const supportedContentTypes = ['image/png', 'image/jpeg', 'image/svg+xml'];
    if (!supportedContentTypes.includes(contentType)) {
      throw new Error(
        `Unsupported content type ${contentType}. Supported types: ${supportedContentTypes.join(
          ','
        )}`
      );
    }

    const response = await fetch(presignedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': contentType,
      },
      body: file,
    });

    if (!response.ok) {
      throw new Error(`S3 upload failed with status ${response.status}`);
    }

    return true;
  } catch (error) {
    console.error('S3 upload error:', error);
    throw error;
  }
};

export const DashboardService = {
  getAll,
  getById,
  createDashboard,
  editDashboard,
  deleteDashboard,
  getUploadUrl,
  uploadFileToS3,
};
